<template>
    <v-flex>
        <div class="float-left mr-10">
            <card-unit :items="[
                { text: t('main_goal_label_id'), value: goal.id },
                { text: t('main_goal_label_title'), value: goal.title },
                { text: t('main_goal_label_type'), value: mainGoalTypeTitle(goal.type) },
                { text: t('main_goal_label_plan_type'), value: goal.planType ? planTypeTitle(goal.planType) : '-' },
                { text: t('main_goal_label_status'), value: mainGoalStatusTitle(goal.status) },
                { text: t('main_goal_label_start_at'), value: goal.startAt ? goal.startAt.format('DD/MM/YYYY') : '-' },
                { text: t('main_goal_label_end_at'), value: goal.endAt ? goal.endAt.format('DD/MM/YYYY') : '-' },
                { text: t('main_goal_label_duration'), value: goal.durationFormatted || '-' },
                { text: t('main_goal_label_distance'), value: goal.distance || '-' },
                { text: t('main_goal_label_target_time'), value: goal.targetTime || '-' },
                { text: t('main_goal_label_stroke'), value: goal.stroke || '-' },
            ]">
                <template #title>{{ t('main_goal_card_title_general') }}</template>
            </card-unit>
        </div>
        <div class="float-left mr-10" v-if="goal.program">
            <card-unit :items="[
                { text: t('main_goal_label_program_id'), value: goal.program.id },
                { text: t('main_goal_label_program'), value: goal.program.name },
                { text: t('main_goal_label_program_description'), value: goal.program.description || '-' },
                { text: t('main_goal_label_program_workout_count'), value: goal.program.workout_count },
                { text: t('main_goal_label_program_workout_types'), value: goal.program.workout_types ? workoutTypesStrTitle(goal.program.workout_types) : '-' },
                { text: t('main_goal_label_program_end_date'), value: goal.program.end_date || '-' },
            ]">
                <template #title>{{ t('main_goal_card_title_program') }}</template>
            </card-unit>
        </div>
        <div class="float-left mr-10" v-if="goal.details && Object.keys(goal.details).length">
            <card-unit :items="detailItems()">
                <template #title>{{ t('main_goal_card_title_details') }}</template>
            </card-unit>
        </div>
        <div class="float-left mr-10" v-if="goal.preferences && Object.keys(goal.preferences).length">
            <card-unit :items="preferenceItems()">
                <template #title>{{ t('main_goal_card_title_preferences') }}</template>
            </card-unit>
        </div>
        <div class="float-left mr-10" v-if="goal.programCard && Object.keys(goal.programCard).length">
            <card-unit :items="programCardItems()">
                <template #title>{{ t('main_goal_card_title_program_card') }}</template>
            </card-unit>
        </div>
        <div class="float-left" v-if="goal.alternativePrograms && goal.alternativePrograms.length">
            <card-unit :items="goal.alternativePrograms.map((p, i) => ({ text: '#' + (i + 1), value: p.name || p.id }))">
                <template #title>{{ t('main_goal_card_title_alternative_programs') }}</template>
            </card-unit>
        </div>
    </v-flex>
</template>

<script lang="ts">
    import { defineComponent } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useEnums } from '@/core/defaults'
    import { UserMainGoal } from '@/core/models/user-main-goal'
    import CardUnit from '@/components/CardUnit.vue'

    export default defineComponent({
        name: 'UserMainGoalView',

        components: {
            CardUnit,
        },

        props: {
            goal: {
                type: UserMainGoal,
                required: true,
            },
        },

        setup(props) {
            const i18n = useI18n()
            const { programTypeTitle, ...restEnums } = useEnums()

            const detailItems = () => {
                if (!props.goal.details) return []
                return Object.entries(props.goal.details).map(([key, value]) => ({
                    text: key,
                    value: typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-'),
                }))
            }

            const preferenceItems = () => {
                if (!props.goal.preferences) return []
                return Object.entries(props.goal.preferences).map(([key, value]) => ({
                    text: key,
                    value: typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-'),
                }))
            }

            const programCardLabelMap: Record<string, string> = {
                program_id:           'main_goal_label_program_card_program_id',
                program_execution_id: 'main_goal_label_program_card_execution_id',
                program:              'main_goal_label_program_card_program_type',
                progress_percentage:  'main_goal_label_program_card_progress',
            }

            const programCardItems = () => {
                if (!props.goal.programCard) return []
                return Object.entries(props.goal.programCard).map(([key, value]) => ({
                    text: programCardLabelMap[key] ? i18n.t(programCardLabelMap[key]) : key,
                    value: key === 'program'
                        ? programTypeTitle(String(value ?? ''))
                        : typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-'),
                }))
            }

            return {
                detailItems,
                preferenceItems,
                programCardItems,
                programTypeTitle,
                ...restEnums,
                ...useI18n(),
            }
        }
    })
</script>